"use client";

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import {
    addVehicles,
    resetParcoVeicoli,
    setCount,
    setFilters,
    setHasMore,
    setPage,
    setVehicles,
} from "@/redux/parcoVeicoliSlice";
import { RootState } from "@/redux/store";
import ProductCard from "@/components/test/ProductCard";
import ProductCartNoleggio from "@/components/ProductCartNoleggio";
import {useDebounceCallback} from "@/hooks/useDebounceCallback";
import {useInView} from "@/hooks/useInView";

// Performance monitoring
const performanceLog = (label: string, startTime: number) => {
    const endTime = performance.now();
    console.log(`${label}: ${(endTime - startTime).toFixed(2)}ms`);
};

// Memoized empty state component
const EmptyStateComponent = React.memo(() => (
    <div className="bg-gray-100 flex flex-col md:flex-row items-center p-6 rounded-lg shadow-lg">
        <div className="flex-shrink-0">
            <svg
                width="75"
                height="65"
                viewBox="0 0 75 65"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-gray-700"
            >
                <path d="M37.42 1.93945V10.4795" stroke="black" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round"/>
                <path d="M73.84 38.3594H65.3" stroke="black" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round"/>
                <path d="M9.54 38.3594H1" stroke="black" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round"/>
                <path d="M63.17 12.6094L57.14 18.6394" stroke="black" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round"/>
                <path d="M17.71 18.6394L11.67 12.6094" stroke="black" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round"/>
                <path
                    d="M37.42 56.8699C46.8751 56.8699 54.54 49.205 54.54 39.7499C54.54 30.2948 46.8751 22.6299 37.42 22.6299C27.9649 22.6299 20.3 30.2948 20.3 39.7499C20.3 49.205 27.9649 56.8699 37.42 56.8699Z"
                    stroke="black"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                />
                <path d="M61.53 63.8594L49.52 51.8594" stroke="black" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round"/>
                <path
                    d="M43.98 42.8096V45.4496C43.98 45.7496 43.74 45.9996 43.43 45.9996H40.79C40.49 45.9996 40.24 45.7596 40.24 45.4496V42.8096H43.98Z"
                    stroke="black"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                />
                <path
                    d="M34.59 42.8096V45.4496C34.59 45.7496 34.35 45.9996 34.04 45.9996H31.4C31.1 45.9996 30.85 45.7596 30.85 45.4496V42.8096H34.59Z"
                    stroke="black"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                />
                <path d="M29.76 38.4395H45.08" stroke="black" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round"/>
                <path
                    d="M43.98 38.4298L41.94 33.8298C41.85 33.6298 41.66 33.5098 41.44 33.5098H33.4C33.18 33.5098 32.99 33.6398 32.9 33.8298L30.86 38.4298V42.8098H43.99V38.4298H43.98Z"
                    stroke="black"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                />
            </svg>
        </div>
        <div className="flex flex-col justify-center text-center md:text-left md:ml-6 mt-4 md:mt-0">
            <h3 className="text-2xl font-semibold text-gray-800">Non hai trovato l'auto che cercavi?</h3>
            <p className="text-gray-600 mt-2">
                La cerchiamo noi per te. Contattaci indicando le specifiche dell'auto che desideri.
            </p>
        </div>
        <div className="mt-4 md:mt-0 md:ml-auto">
            <Link
                href="/richiesta-veicolo"
                hrefLang="it"
                title="Richiesta veicolo"
                className="bg-primary text-white font-medium py-4 px-6 rounded-lg hover:bg-primary/80 transition"
            >
                Invia richiesta
            </Link>
        </div>
    </div>
));

EmptyStateComponent.displayName = 'EmptyStateComponent';

// Memoized loading component
const LoadingComponent = React.memo(() => (
    <div className="flex justify-center items-center py-8">
        <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">Caricamento...</span>
        </div>
    </div>
));

LoadingComponent.displayName = 'LoadingComponent';

// Memoized vehicle item component
const VehicleItem = React.memo(({
                                    vehicle,
                                    index,
                                    typeSearch,
                                    ItemComponent
                                }: {
    vehicle: any;
    index: number;
    typeSearch: string;
    ItemComponent: React.ComponentType<{ product: any }>;
}) => {
    const key = `${vehicle.id || index}-${vehicle.slug || ''}`;

    if (typeSearch === 'vendita') {
        return vehicle.tipoOfferta === 'noleggio' ? (
            <ProductCartNoleggio key={key} product={vehicle} showBadge={true} />
        ) : (
            <ProductCard key={key} product={vehicle} />
        );
    }

    return <ItemComponent key={key} product={vehicle} />;
});

VehicleItem.displayName = 'VehicleItem';

interface InfiniteScrollProps {
    initialVehicles: any[];
    initialCount: number;
    filters: string;
    pageSize: number;
    tipoListino: string;
    ItemComponent: React.ComponentType<{ product: any }>;
    typeSearch: string;
}

export default function OptimizedInfiniteScroll({
                                                    initialVehicles,
                                                    initialCount,
                                                    filters,
                                                    pageSize,
                                                    tipoListino,
                                                    ItemComponent,
                                                    typeSearch
                                                }: InfiniteScrollProps) {
    const dispatch = useDispatch();
    const { vehicles, count, page, hasMore } = useSelector(
        (state: RootState) => state.parcoVeicoli
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Memoize parsed filters to avoid unnecessary re-parsing
    const parsedFilters = useMemo(() => {
        try {
            return JSON.parse(filters);
        } catch (e) {
            console.error('Error parsing filters:', e);
            return {};
        }
    }, [filters]);

    const currentFilters = useSelector((state: RootState) => state.parcoVeicoli.filters);
    const prevFiltersRef = useRef(filters);

    // Intersection observer for infinite scroll
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0.1,
        rootMargin: '200px', // Start loading 200px before the element is visible
    });

    // Debounced load more function to prevent rapid API calls
    const debouncedLoadMore = useDebounceCallback(
        async () => {
            if (loading || !hasMore || error) return;

            const startTime = performance.now();
            setLoading(true);
            setError(null);

            try {
                const query = new URLSearchParams({
                    ...parsedFilters,
                    order: parsedFilters.order || "prezzo_DESC",
                    page: (page + 1).toString(),
                    pageSize: pageSize.toString(),
                    tipoUtenteListino: tipoListino,
                    typeSearch: typeSearch,
                }).toString();

                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

                const res = await fetch(
                    `${process.env.NEXT_PUBLIC_API_URL}/api/articoli/lista-veicoli?${query}`,
                    {
                        signal: controller.signal,
                        method: 'GET',
                    }
                );

                clearTimeout(timeoutId);

                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }

                const data = await res.json();

                performanceLog('API fetch', startTime);

                if (data.data && Array.isArray(data.data)) {
                    dispatch(addVehicles(data.data));
                    dispatch(setPage(page + 1));
                    dispatch(setHasMore(data.data.length >= pageSize));
                } else {
                    dispatch(setHasMore(false));
                }
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    console.log('Request was aborted');
                } else {
                    console.error("Error loading more vehicles:", error);
                    setError('Errore nel caricamento dei veicoli. Riprova.');
                }
            } finally {
                setLoading(false);
            }
        },
        300 // 300ms debounce
    );

    // Load more when in view
    useEffect(() => {
        if (inView && hasMore && !loading && !error) {
            debouncedLoadMore();
        }
    }, [inView, hasMore, loading, error, debouncedLoadMore]);

    // Handle filter changes
    useEffect(() => {
        const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(parsedFilters);

        if (filtersChanged || prevFiltersRef.current !== filters) {
            console.log("Filters changed, updating Redux...");

            // Reset error state
            setError(null);

            // Update Redux state
            dispatch(setFilters(parsedFilters));
            dispatch(resetParcoVeicoli());
            dispatch(setVehicles(initialVehicles));
            dispatch(setCount(initialCount));
            dispatch(setPage(1));
            dispatch(setHasMore(initialVehicles.length < initialCount));

            prevFiltersRef.current = filters;
        } else if (vehicles.length === 0 && page === 1) {
            console.log("First load, loading vehicles...");
            dispatch(setVehicles(initialVehicles));
            dispatch(setCount(initialCount));
            dispatch(setPage(1));
            dispatch(setHasMore(initialVehicles.length < initialCount));
        }
    }, [dispatch, parsedFilters, initialVehicles, initialCount, vehicles.length, page, filters, currentFilters]);

    // Memoized vehicle grid to prevent unnecessary re-renders
    const vehicleGrid = useMemo(() => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-4 xl:gap-4 mb-10">
            {vehicles.map((vehicle: any, index: number) => (
                <VehicleItem
                    key={`${vehicle.id || index}-${vehicle.slug || ''}`}
                    vehicle={vehicle}
                    index={index}
                    typeSearch={typeSearch}
                    ItemComponent={ItemComponent}
                />
            ))}
        </div>
    ), [vehicles, typeSearch, ItemComponent]);

    // Error retry handler
    const handleRetry = useCallback(() => {
        setError(null);
        debouncedLoadMore();
    }, [debouncedLoadMore]);

    return (
        <div>
            {vehicleGrid}

            {/* Loading/Error states */}
            <div ref={loadMoreRef as React.RefObject<HTMLDivElement>}>
                {loading && <LoadingComponent />}

                {error && (
                    <div className="flex flex-col items-center py-8">
                        <p className="text-red-600 mb-4">{error}</p>
                        <button
                            onClick={handleRetry}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
                        >
                            Riprova
                        </button>
                    </div>
                )}

                {!hasMore && !loading && vehicles.length > 0 && <EmptyStateComponent />}
            </div>
        </div>
    );
}
